// src/frontend/js/modern/core/sync.js
/**
 * SyncManager — Cross-tab synchronization with Leader Election.
 *
 * Architecture:
 *  - One tab is elected as "leader" via BroadcastChannel.
 *  - Only the leader makes server requests (syncAll).
 *  - Non-leader tabs request sync from the leader.
 *  - The leader broadcasts sync results to all tabs.
 *  - If the leader dies, a new leader is elected automatically.
 *  - Fallback: if BroadcastChannel is unavailable, every tab
 *    acts as its own leader (original behavior).
 *
 * Message protocol (BroadcastChannel 'wlfmc_sync'):
 *  - tab_announce      : New tab announces itself
 *  - leader_heartbeat  : Leader announces it is alive (every 3s)
 *  - leader_claim      : A tab claims leadership
 *  - leader_resign     : Leader is closing (beforeunload)
 *  - request_sync      : A tab asks the leader to sync
 *  - sync_result       : Leader broadcasts server data to all tabs
 *  - sync_started      : Leader notifies sync started
 *  - sync_failed       : Leader notifies sync failed
 *  - incremental_update: Leader broadcasts specific changes (not full sync)
 */
import { eventBus, EVENTS } from './event-bus.js';
import { STORAGE_KEYS } from '../shared/constants.js';
import { syncGateway } from './sync-gateway.js';
import { queueManager } from './queue.js';
import { stateCache } from './state-cache.js';
import { displayStore } from './storage/display-store.js';
import { fragmentManager } from './fragment-manager.js';

const LAST_SYNC_KEY = 'wlfmc_last_sync';

// Leader Election timing constants (milliseconds)
const HEARTBEAT_INTERVAL = 3000;
const LEADER_TIMEOUT = 7500;
const ELECTION_WAIT = 600;
const STARTUP_WAIT = 900;

// Cache validation timing
const SYNC_INTERVAL = 10 * 60 * 1000; // 10 minutes
const VISIBILITY_THROTTLE = 10000;

class SyncManager {
	constructor() {
		this.isSyncing = false;
		this._pendingSync = false;
		this._lastSyncTimestamp = null;
		this._broadcastDebounce = null;
		this._remoteSyncTimer = null;
		this._boundStorageHandler = null;
		this._boundVisibilityHandler = null;
		this._lastVisibilityCheck = 0;

		// Leader Election state
		this._tabId = this._generateTabId();
		this._isLeader = false;
		this._leaderId = null;
		this._leaderLastSeen = 0;
		this._heartbeatTimer = null;
		this._healthCheckTimer = null;
		this._periodicSyncTimer = null;
		this._channel = null;
		this._channelAvailable = false;

		// Sync request tracking to prevent duplicate requests
		this._syncRequestPending = false;
		this._syncRequestTimer = null;
	}

	/* ═══════════════════════════════════════════
	   Initialization
	════════════════════════════════════════════ */
	init() {
		this._initChannel();
		this._initLeaderElection();

		// Fallback: listen to storage events for browsers without BroadcastChannel
		this._boundStorageHandler = this._handleStorageEvent.bind(this);
		window.addEventListener('storage', this._boundStorageHandler);

		// Resign leadership when this tab closes
		window.addEventListener('beforeunload', () => {
			if (this._isLeader) {
				this._send({ type: 'leader_resign', tabId: this._tabId });
			}
		});

		// Cache validation on tab visibility change (replaces CacheValidator)
		this._boundVisibilityHandler = this._handleVisibilityChange.bind(this);
		document.addEventListener('visibilitychange', this._boundVisibilityHandler);

		console.log('[SyncManager] Initialized (Leader Election mode)', {
			tabId: this._tabId,
			channelAvailable: this._channelAvailable
		});
	}

	_generateTabId() {
		try {
			return crypto.randomUUID();
		} catch {
			return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
		}
	}

	_initChannel() {
		try {
			this._channel = new BroadcastChannel('wlfmc_sync');
			this._channelAvailable = true;
			this._channel.onmessage = this._handleChannelMessage.bind(this);
			console.log('[SyncManager] BroadcastChannel initialized');
		} catch (e) {
			this._channelAvailable = false;
			console.warn('[SyncManager] BroadcastChannel unavailable — fallback mode');
		}
	}

	/* ═══════════════════════════════════════════
	   Cache Validation (merged from CacheValidator)
	   Runs when the user returns to this tab.
	═════════════════════════════════════════════ */
	_handleVisibilityChange() {
		if (document.hidden) return;

		const now = Date.now();
		if (now - this._lastVisibilityCheck < VISIBILITY_THROTTLE) return;
		this._lastVisibilityCheck = now;

		// Force sync if the server requires fresh data (e.g., fragments mode).
		const updateWishlistsData = window.wlfmc_l10n?.update_wishlists_data;
		if (updateWishlistsData) {
			console.log('[SyncManager] Visibility change: forcing sync (update_wishlists_data)');
			this.syncAll();
			return;
		}

		// Language change: purge all cached data and re-sync.
		const currentLang = window.wlfmc_l10n?.lang || '';
		const storedLang = localStorage.getItem(STORAGE_KEYS.LANG_HASH) || '';
		if (currentLang && storedLang && currentLang !== storedLang) {
			console.log('[SyncManager] Language changed, purging cache');
			localStorage.setItem(STORAGE_KEYS.PRODUCTS_HASH, '');
			localStorage.setItem(STORAGE_KEYS.WAITLIST_HASH, '');
			localStorage.setItem(STORAGE_KEYS.LANG_HASH, '');
			stateCache.clear();
			try {
				localStorage.removeItem(STORAGE_KEYS.WISHLIST);
				localStorage.removeItem(STORAGE_KEYS.WAITLIST);
				localStorage.removeItem(STORAGE_KEYS.MULTI_LIST);
			} catch (e) { /* noop */ }
			this.syncAll({ forceLocal: true });
			return;
		}

		// Stale cache: re-sync if older than SYNC_INTERVAL.
		const lastSync = localStorage.getItem(LAST_SYNC_KEY);
		const stale = !lastSync || (now - parseInt(lastSync, 10)) > SYNC_INTERVAL;
		if (stale) {
			console.log('[SyncManager] Cache stale, syncing with server...');
			this.syncAll();
		}
	}

	/* ═══════════════════════════════════════════
	   Leader Election
	════════════════════════════════════════════ */
	_initLeaderElection() {
		if (!this._channelAvailable) {
			// Fallback: this tab is its own leader
			console.log('[SyncManager] No BroadcastChannel — becoming leader (fallback mode)');
			this._isLeader = true;
			this._leaderId = this._tabId;
			this._startPeriodicSync();
			return;
		}

		// Announce ourselves and wait for an existing leader
		this._send({ type: 'tab_announce', tabId: this._tabId, timestamp: Date.now() });
		setTimeout(() => {
			if (!this._leaderId) {
				console.log('[SyncManager] No leader found, claiming leadership');
				this._claimLeadership();
			} else {
				console.log('[SyncManager] Leader exists:', this._leaderId);
			}
		}, STARTUP_WAIT);

		// Periodically check if the leader is still alive
		this._healthCheckTimer = setInterval(() => {
			this._checkLeaderHealth();
		}, LEADER_TIMEOUT);
	}

	_claimLeadership() {
		this._isLeader = true;
		this._leaderId = this._tabId;
		this._leaderLastSeen = Date.now();
		this._send({
			type: 'leader_claim',
			tabId: this._tabId,
			timestamp: Date.now(),
		});
		this._startHeartbeat();
		this._startPeriodicSync();
		console.log('[SyncManager] This tab is now the LEADER');
	}

	_startHeartbeat() {
		if (this._heartbeatTimer) clearInterval(this._heartbeatTimer);
		this._heartbeatTimer = setInterval(() => {
			if (this._isLeader) {
				this._send({
					type: 'leader_heartbeat',
					tabId: this._tabId,
					timestamp: Date.now(),
				});
			}
		}, HEARTBEAT_INTERVAL);
	}

	_startPeriodicSync() {
		if (this._periodicSyncTimer) clearInterval(this._periodicSyncTimer);
		this._periodicSyncTimer = setInterval(() => {
			if (this._isLeader && !document.hidden) {
				console.log('[SyncManager] Periodic sync (leader)');
				this.syncAll();
			}
		}, SYNC_INTERVAL);
	}

	_checkLeaderHealth() {
		if (this._isLeader) return;
		const now = Date.now();
		if (this._leaderId && (now - this._leaderLastSeen > LEADER_TIMEOUT)) {
			console.log('[SyncManager] Leader appears dead — re-electing');
			this._leaderId = null;
			this._claimLeadership();
		}
	}

	_stepDown() {
		this._isLeader = false;
		if (this._heartbeatTimer) {
			clearInterval(this._heartbeatTimer);
			this._heartbeatTimer = null;
		}
		if (this._periodicSyncTimer) {
			clearInterval(this._periodicSyncTimer);
			this._periodicSyncTimer = null;
		}
		console.log('[SyncManager] Stepped down from leadership');
	}

	/* ═══════════════════════════════════════════
	   BroadcastChannel Message Handler
	════════════════════════════════════════════ */
	_handleChannelMessage(event) {
		const msg = event.data;
		if (!msg || !msg.type) return;

		switch (msg.type) {
			case 'tab_announce':
				// A new tab joined. If we are leader, send heartbeat immediately.
				if (this._isLeader && msg.tabId !== this._tabId) {
					this._send({
						type: 'leader_heartbeat',
						tabId: this._tabId,
						timestamp: Date.now(),
					});
				}
				break;
			case 'leader_heartbeat':
				if (msg.tabId !== this._tabId) {
					this._leaderId = msg.tabId;
					this._leaderLastSeen = Date.now();
					// If we also think we are leader, the lower tabId wins
					if (this._isLeader && msg.tabId < this._tabId) {
						console.log('[SyncManager] Another tab has priority — stepping down');
						this._stepDown();
					}
				}
				break;
			case 'leader_claim':
				if (msg.tabId !== this._tabId) {
					this._leaderId = msg.tabId;
					this._leaderLastSeen = Date.now();
					if (this._isLeader && msg.tabId < this._tabId) {
						console.log('[SyncManager] Another tab claimed leadership — stepping down');
						this._stepDown();
					}
				}
				break;
			case 'leader_resign':
				if (msg.tabId === this._leaderId) {
					console.log('[SyncManager] Leader resigned — claiming leadership');
					this._leaderId = null;
					this._claimLeadership();
				}
				break;
			case 'request_sync':
				// Only the leader responds to sync requests from other tabs
				if (this._isLeader && msg.tabId !== this._tabId) {
					console.log('[SyncManager] Received sync request from tab:', msg.tabId);
					this._scheduleRemoteSync();
				}
				break;
			case 'sync_result':
				// Leader broadcasted server data — apply it locally
				if (msg.tabId !== this._tabId && msg.data) {
					console.log('[SyncManager] Received sync result from leader');
					this._applySyncData(msg.data, { remoteTriggered: true });
					this._lastSyncTimestamp = Date.now();
					localStorage.setItem(LAST_SYNC_KEY, String(this._lastSyncTimestamp));
					localStorage.setItem(
						STORAGE_KEYS.LANG_HASH,
						window.wlfmc_l10n?.lang || ''
					);
					this._syncRequestPending = false;
				}
				break;
			case 'sync_started':
				if (msg.tabId !== this._tabId) {
					eventBus.emit(EVENTS.SYNC_STARTED);
				}
				break;
			case 'sync_failed':
				if (msg.tabId !== this._tabId) {
					eventBus.emit(EVENTS.SYNC_FAILED, { error: msg.error });
					this._syncRequestPending = false;
				}
				break;
			// NEW: Incremental update from leader (avoid full sync for small changes)
			case 'incremental_update':
				if (msg.tabId !== this._tabId && msg.update) {
					console.log('[SyncManager] Received incremental update', msg.update);
					this._applyIncrementalUpdate(msg.update);
				}
				break;
		}
	}

	_send(message) {
		if (!this._channelAvailable || !this._channel) return;
		try {
			this._channel.postMessage(message);
		} catch (e) {
			console.warn('[SyncManager] Channel send failed:', e);
		}
	}

	/* ═══════════════════════════════════════════
	   Cross-Tab Broadcast (outgoing)
	════════════════════════════════════════════ */
	/**
	 * Notify other tabs that data has changed.
	 *
	 * IMPORTANT: This does NOT trigger sync in the current tab!
	 * The current tab already has the correct state after a successful operation.
	 * Only other tabs need to be notified.
	 *
	 * Uses BroadcastChannel when available, localStorage as fallback.
	 */
	broadcastUpdate(update) {
		try {
			clearTimeout(this._broadcastDebounce);
			this._broadcastDebounce = setTimeout(() => {
				if (this._channelAvailable) {
					// Notify other tabs via BroadcastChannel
					// Send incremental update instead of requesting full sync
					this._send({
						type: 'incremental_update',
						tabId: this._tabId,
						timestamp: Date.now(),
						update: {
							type: update.type,
							productId: update.productId,
							data: update.data,
						},
					});
					console.log('[SyncManager] Broadcasted incremental update:', update.type);
				} else {
					// Fallback: localStorage storage event
					const payload = {
						type: update.type,
						data: update.data,
						timestamp: Date.now(),
						version: 1,
						source: 'broadcast',
					};
					localStorage.setItem(STORAGE_KEYS.SYNC_DATA, JSON.stringify(payload));
					sessionStorage.setItem(
						STORAGE_KEYS.SYNC_DATA + '_sent',
						String(Date.now())
					);
					console.log('[SyncManager] Broadcasted via localStorage (fallback)');
				}
			}, 50);
		} catch (error) {
			console.warn('[SyncManager] Broadcast failed:', error);
		}
	}

	/* ═══════════════════════════════════════════
	   Incremental Update Handler
	════════════════════════════════════════════ */
	/**
	 * Apply incremental updates from other tabs without full sync.
	 * This is much more efficient than requesting a full sync for every small change.
	 */
	/**
	 * Apply incremental updates from other tabs without full sync.
	 * This is much more efficient than requesting a full sync for every small change.
	 */
	_applyIncrementalUpdate(update) {
		const { type, productId, data } = update;
		// The data payload may be the serverResponse directly (from standard wishlist events)
		// or wrapped in another object (from multi-list events). Normalize it here.
		const serverResponse = data?.data || data;

		switch (type) {
			case EVENTS.WISHLIST_ADDED:
				if (productId && serverResponse) {
					const module = this._getModuleFromEvent(type);
					if (module) {
						console.log('[SyncManager] Applying incremental add for:', module, productId);
						// Extract and store display data separately in DisplayStore
						if (serverResponse.display) {
							displayStore.set(module, productId, serverResponse.display);
						}
						// Clean up server response metadata to form a proper item object
						const {
							display, success, count, message, result,
							load_automation_nonce, customer_id, wishlist_url,
							...cleanData
						} = serverResponse;
						syncGateway.upsertItem(module, productId, {
							...cleanData,
							product_id: productId, // Ensure product_id is explicitly set for UI reconciliation
							fromOtherTab: true,
						});
						this._reloadTableIfNeeded(module);
					}
				}
				break;
			case EVENTS.WISHLIST_REMOVED:
				if (productId) {
					const module = this._getModuleFromEvent(type);
					if (module) {
						console.log('[SyncManager] Applying incremental remove for:', module, productId);
						syncGateway.removeItem(module, productId);
						this._reloadTableIfNeeded(module);
					}
				}
				break;
			// For complex operations (multi-list, list create/delete/etc), request full sync
			default:
				console.log('[SyncManager] Complex operation or multi-list update — requesting full sync');
				this._scheduleRemoteSync();
				break;
		}
	}

	/**
	 * Check if the current page contains a table/wrapper for the given module
	 * and reload its fragments to reflect the cross-tab change.
	 *
	 * This ensures that when a user adds/removes an item in Tab A,
	 * the main table in Tab B (if viewing that list) is refreshed.
	 */
	_reloadTableIfNeeded(module) {
		let selector = '';
		let fragmentScope = '';
		switch (module) {
			case 'wishlist':
				selector = '.wlfmc-default-table';
				fragmentScope = 'wishlist';
				break;
			case 'waitlist':
				selector = '.wlfmc-waitlist-table';
				fragmentScope = 'waitlist';
				break;
			case 'saveForLater':
				selector = '.wlfmc-save-for-later-table';
				fragmentScope = 'save_for_later';
				break;
			case 'multiList':
				// Multi-list tables have data-id attributes (one per list)
				selector = '.wlfmc-multi-list-table[data-id]';
				fragmentScope = ''; // Load all fragments for multi-list
				break;
		}
		if (selector && document.querySelector(selector)) {
			console.log(`[SyncManager] Table detected for ${module} — reloading fragments`);
			// fragmentManager has built-in debounce (300ms), so multiple rapid calls are safe
			fragmentManager.loadFragments(fragmentScope ? { s: fragmentScope } : {});
		}
	}

	_getModuleFromEvent(eventType) {
		if (eventType.includes('wishlist')) return 'wishlist';
		if (eventType.includes('waitlist')) return 'waitlist';
		if (eventType.includes('multiList')) return 'multiList';
		if (eventType.includes('saveForLater')) return 'saveForLater';
		return null;
	}

	/* ═══════════════════════════════════════════
	   Storage Event Fallback
	════════════════════════════════════════════ */
	_handleStorageEvent(event) {
		// Only used when BroadcastChannel is unavailable
		if (this._channelAvailable) return;
		if (event.key !== STORAGE_KEYS.SYNC_DATA) return;
		if (!event.newValue) return;
		try {
			const data = JSON.parse(event.newValue);
			const sentTime = sessionStorage.getItem(STORAGE_KEYS.SYNC_DATA + '_sent');
			// Ignore our own broadcasts
			if (sentTime && Math.abs(parseInt(sentTime, 10) - data.timestamp) < 100) {
				return;
			}
			console.log('[SyncManager] Received storage event:', data.type);
			this._processSyncData(data);
		} catch (error) {
			console.warn('[SyncManager] Storage event error:', error);
		}
	}

	_processSyncData(data) {
		if (data.type === 'cache_update') return;
		this._scheduleRemoteSync();
	}

	_scheduleRemoteSync() {
		clearTimeout(this._remoteSyncTimer);
		this._remoteSyncTimer = setTimeout(() => {
			if (queueManager.getStatus().isProcessing) {
				console.log('[SyncManager] Queue busy, rescheduling sync');
				this._scheduleRemoteSync();
				return;
			}
			this.syncAll({ remoteTriggered: true });
		}, 300);
	}

	/* ═══════════════════════════════════════════
	   Server Sync (single path)
	════════════════════════════════════════════ */
	/**
	 * Synchronize with the server.
	 *
	 * If this tab is the leader (or no leader exists yet, or BroadcastChannel
	 * is unavailable), it performs the actual HTTP request.
	 * Otherwise it delegates to the leader via BroadcastChannel.
	 *
	 * @param {object} options
	 * @param {boolean} options.remoteTriggered - Whether triggered by cross-tab signal
	 * @param {boolean} options.forceLocal - Force this tab to sync locally
	 */
	async syncAll(options = {}) {
		// Determine whether this tab should sync locally
		const shouldSyncLocally =
			!this._channelAvailable ||
			this._isLeader ||
			!this._leaderId ||
			options.forceLocal;

		if (!shouldSyncLocally) {
			// Delegate to the leader
			if (this._syncRequestPending) {
				console.log('[SyncManager] Sync request already pending');
				return;
			}
			console.log('[SyncManager] Delegating sync to leader:', this._leaderId);
			this._syncRequestPending = true;
			this._send({
				type: 'request_sync',
				tabId: this._tabId,
				timestamp: Date.now(),
			});
			// Timeout: if leader doesn't respond in 10s, force local sync
			this._syncRequestTimer = setTimeout(() => {
				if (this._syncRequestPending) {
					console.warn('[SyncManager] Sync request timeout — forcing local sync');
					this._syncRequestPending = false;
					this.syncAll({ forceLocal: true });
				}
			}, 10000);
			return;
		}

		// Leader path: perform the actual sync
		if (this.isSyncing) {
			console.log('[SyncManager] Sync already in progress, queueing');
			this._pendingSync = true;
			return;
		}

		this.isSyncing = true;
		eventBus.emit(EVENTS.SYNC_STARTED);

		// Notify other tabs that sync started
		if (this._isLeader && this._channelAvailable) {
			this._send({
				type: 'sync_started',
				tabId: this._tabId,
				timestamp: Date.now(),
			});
		}

		try {
			const l10n = window.wlfmc_l10n || {};
			const action =
				(l10n.actions && l10n.actions.modern_sync) || 'wlfmc_modern_sync';
			const isRest = l10n.ajax_mode === 'rest_api';
			const url = l10n.ajax_url;
			const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
			if (isRest && l10n.nonce) {
				headers['X-WP-Nonce'] = l10n.nonce;
			}

			console.log('[SyncManager] Starting sync request');
			const response = await fetch(url, {
				method: 'POST',
				headers,
				body: new URLSearchParams({
					action,
					nonce: l10n.ajax_nonce.sync_nonce || '',
				}),
			});
			const result = await response.json();

			if (result.success) {
				console.log('[SyncManager] Sync successful, applying data');
				this._applySyncData(result.data, options);
				this._lastSyncTimestamp = Date.now();
				localStorage.setItem(LAST_SYNC_KEY, String(this._lastSyncTimestamp));
				localStorage.setItem(
					STORAGE_KEYS.LANG_HASH,
					window.wlfmc_l10n?.lang || ''
				);
				// Broadcast the result to all other tabs
				if (this._isLeader && this._channelAvailable) {
					this._send({
						type: 'sync_result',
						tabId: this._tabId,
						data: result.data,
						timestamp: Date.now(),
					});
				}
			} else {
				throw new Error(result.data?.message || 'Sync failed');
			}
		} catch (error) {
			console.warn('[SyncManager] Sync failed:', error);
			eventBus.emit(EVENTS.SYNC_FAILED, { error: error.message });
			// Notify other tabs about the failure
			if (this._isLeader && this._channelAvailable) {
				this._send({
					type: 'sync_failed',
					tabId: this._tabId,
					error: error.message,
					timestamp: Date.now(),
				});
			}
		}

		this.isSyncing = false;
		this._syncRequestPending = false;
		if (this._syncRequestTimer) {
			clearTimeout(this._syncRequestTimer);
			this._syncRequestTimer = null;
		}

		if (this._pendingSync) {
			this._pendingSync = false;
			console.log('[SyncManager] Processing queued sync');
			await this.syncAll(options);
		}
	}

	/**
	 * Apply server data through the single SyncGateway.
	 */
	_applySyncData(data, options = {}) {
		syncGateway.applyServerData(data, {
			remoteTriggered: !!options.remoteTriggered,
		});
	}

	/* ═══════════════════════════════════════════
	   Public API
	════════════════════════════════════════════ */
	getLastSyncTime() {
		return this._lastSyncTimestamp;
	}

	get isLeader() {
		return this._isLeader;
	}

	get leaderId() {
		return this._leaderId;
	}

	/**
	 * Manually trigger a sync (useful for testing/debugging)
	 */
	forceSync() {
		console.log('[SyncManager] Manual sync triggered');
		this.syncAll({ forceLocal: true });
	}

	destroy() {
		clearTimeout(this._remoteSyncTimer);
		clearTimeout(this._broadcastDebounce);
		clearTimeout(this._syncRequestTimer);
		if (this._heartbeatTimer) {
			clearInterval(this._heartbeatTimer);
			this._heartbeatTimer = null;
		}
		if (this._healthCheckTimer) {
			clearInterval(this._healthCheckTimer);
			this._healthCheckTimer = null;
		}
		if (this._periodicSyncTimer) {
			clearInterval(this._periodicSyncTimer);
			this._periodicSyncTimer = null;
		}
		if (this._boundStorageHandler) {
			window.removeEventListener('storage', this._boundStorageHandler);
			this._boundStorageHandler = null;
		}
		if (this._boundVisibilityHandler) {
			document.removeEventListener('visibilitychange', this._boundVisibilityHandler);
			this._boundVisibilityHandler = null;
		}
		if (this._channel) {
			this._channel.close();
			this._channel = null;
		}
		console.log('[SyncManager] Destroyed');
	}
}

export const syncManager = new SyncManager();
export default syncManager;
